import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { virtualize } from '@adornis/chemistry/directives/when-seen.js';
import type { EntityPickerOptions } from '@adornis/entity-picker/client/entity-picker-options.js';
import '@adornis/entity-picker/client/x-entity-picker.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { LearningDataAPI } from '@chancenwerk/campus-learning-content/api/LearningDataAPI.js';
import { CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { CampusArea } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import '@chancenwerk/components/notifications/chancen-component-notification-list.js';
import { CampusLessonFilter } from '@chancenwerk/db/filter/CampusLessonFilter.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { KPFAPI } from '@chancenwerk/master-data-management/kpf/api/KPFAPI.js';
import { KPFAspect } from '@chancenwerk/master-data-management/kpf/entities/aspects/KPFAspect.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { UserAPI } from '@chancenwerk/master-data-management/users/api/UserAPI.js';
import { ExternerMitarbeiter } from '@chancenwerk/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import '../components/campus-component-section-lesson-list.js';
import { teacherDashboardEmployeeFilter } from '../globalSubject.js';
import { CampusRoutes } from '../routes.js';
import './campus-page-no-permission.js';

@customElement('campus-page-teacher-dashboard-employee-list')
export class CampusPageTeacherDashboardEmployeeList extends ChemistryLitElement {
  private readonly _filter = new RXController(this, teacherDashboardEmployeeFilter);

  private readonly _pagination = new SubscriptionPagination<
    Mitarbeiter | VerwaltungsMitarbeiter | ExternerMitarbeiter,
    Array<ChancenwerkUserFilter | undefined>
  >(
    (skip, limit, ...args) => UserAPI.subscribeByFilter(skip, limit, args[0] as ChancenwerkUserFilter, false),
    [
      new ChancenwerkUserFilter({
        userClass: [Mitarbeiter._class, ExternerMitarbeiter._class, VerwaltungsMitarbeiter._class],
        aspects: [new BooleanAspect({ customID: 'active', value: true })],
      }),
    ],
    {
      vorname: 1,
      nachname: 1,
      activeRelations: {
        object: {
          school: {
            name: 1,
          },
        },
      },
    },
    20,
    20,
  );

  @state() private readonly _data = new RXController(this, this._pagination.data);

  private readonly size = new WindowSizeController(this);

  override styles = () => [
    {
      ':host': {
        // height: '100%',
        width: '100%',
      },
    },
  ];

  override render() {
    return permissionGuard(ChancenPermission.campus.VIEW_TEACHER_DASHBOARD, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _handleSearchUpdate(value: Maybe<string>) {
    this._filter.value.search = value;
    this._pagination.updateParameters(this._filter.value);
  }

  private _content() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex space="lg">
          <cr-h2> Teacher Dashboard </cr-h2>
          <cr-card space="md">
            <cr-flex horizontal space="md">
              <cr-input
                flex
                placeholder="Name"
                clearable
                .value=${this._filter.value.search}
                @value-picked=${(e: ValueEvent<Maybe<string>>) => this._handleSearchUpdate(e.detail.value)}
                @submit=${(e: ValueEvent<Maybe<string>>) => this._handleSearchUpdate(e.detail.value)}
              >
              </cr-input>
              <x-entity-picker
                flex
                placeholder="KPF"
                clearable
                .value=${(this._filter.value.aspects.find(a => a.customID === 'kpfs') as Maybe<KPFAspect>)?.value}
                .options=${{
                  class: KPF,
                  operation: KPFAPI.subscribeByFilter,
                  selectionSet: {
                    name: 1,
                    kuerzel: 1,
                    produkt: { _class: 1, kuerzel: 1 },
                    school: { name: 1, kuerzel: 1 },
                  },
                  filter: new AdornisFilter({}),
                }}
                @entity-picked=${(e: ValueEvent<Maybe<KPF>>) => {
                  this._filter.value.aspects = this._filter.value.aspects.filter(a => a.customID !== 'kpfs');
                  if (e.detail.value) {
                    this._filter.value.aspects.push(
                      new KPFAspect({
                        customID: 'kpfs',
                        value: e.detail.value._id,
                      }),
                    );
                  }

                  this._pagination.updateParameters(this._filter.value);
                }}
              ></x-entity-picker>
            </cr-flex>
            <cr-flex horizontal space="md" w100>
              <x-multi-entity-picker
                ${css({ width: '100%' })}
                clearable
                placeholder="Bearbeitete Lektionen"
                .value=${(
                  this._filter.value.aspects.find(a => a.customID === 'lessons') as Maybe<StringAspect>
                )?.value?.split(',') ?? []}
                @entity-picked=${(e: ValueEvent<Maybe<CampusLesson[]>>) => {
                  this._filter.value.aspects = this._filter.value.aspects.filter(a => a.customID !== 'lessons');
                  if (e.detail.value) {
                    this._filter.value.aspects.push(
                      new StringAspect({
                        customID: 'lessons',
                        value: e.detail.value.map(a => a._id).join(','),
                      }),
                    );
                  }

                  this._pagination.updateParameters(this._filter.value);
                }}
                .renderString=${(item: Maybe<CampusLesson>) => item?.name}
                .options=${{
                  class: CampusLesson,
                  operation: LearningDataAPI.subscribeLessonsByFilter,
                  selectionSet: { name: 1, _id: 1 },
                  filter: new CampusLessonFilter({ sectionArea: CampusArea.EMPLOYEES, onlyPublishedLessons: true }),
                } satisfies EntityPickerOptions<typeof CampusLesson>}
              >
              </x-multi-entity-picker>
            </cr-flex>
          </cr-card>
        </cr-flex>

        ${this._data.value?.length === 0 ? html`<cr-text>Keine Mitarbeiter gefunden!</cr-text>` : nothing}

        <cr-grid columns="1fr 1fr 1fr" space="md">
          ${repeat(
            this._data.value ?? [],
            s => s._id,
            (s, index) => {
              return virtualize(
                html`<cr-card padding="md">
                  <cr-flex horizontal space-between crossaxis-center>
                    <cr-flex horizontal space="md" crossaxis-center>
                      <cr-icon ${css({ fontSize: '2.2rem' })}>person_raised_hand</cr-icon>
                      <cr-flex space="md">
                        <cr-text bold ${css({ fontSize: '1.1rem' })}>${s.vorname} ${s.nachname}</cr-text>
                        <cr-flex space="sm" ${css({ opacity: 0.75 })}>
                          ${s.activeRelations
                            ?.slice(0, 2)
                            .map(r => html`<cr-text>${(r.object as Maybe<KPF>)?.school?.name ?? 'Kein KPF'}</cr-text>`)}
                          ${(s.activeRelations?.length ?? 0) > 2 ? html`<cr-text>und mehr...</cr-text>` : nothing}
                        </cr-flex>
                      </cr-flex>
                    </cr-flex>
                    <cr-button
                      mode="fab"
                      icon="chevron_right"
                      href=${`${CampusRoutes.teacherDashboardStudent}profile/employee_${s._id}`}
                    >
                    </cr-button>
                  </cr-flex>
                </cr-card>`,
                oldHeight => html` <div ${css({ height: oldHeight + 'px' })}></div> `,
                {
                  dontUnload: true,
                  onRender: () => {
                    // The current rendered item is near the end of the list && we havent already loaded the new items
                    if (
                      index === (this._data.value ?? []).length - 2 &&
                      this._pagination.totalRequested.getValue() < index + 4
                    )
                      this._pagination.next();
                  },
                },
              );
            },
          )}
        </cr-grid>

        <cr-flex horizontal space="lg" wrap> </cr-flex>
        <cr-flex w100 ${css({ height: '100px' })}></cr-flex>
      </cr-flex>
    `;
  }
}
